// src/pages/auth/SelectPortal.tsx
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiAlertCircle,
  FiShield,
  FiTruck,
  FiUsers,
  FiBriefcase,
  FiChevronRight,
} from "react-icons/fi";
import type { IconType } from "react-icons";

import { selectRole, type AvailableRole } from "@/api/client";
import { verifySession } from "@/api/auth";
import { resolvePostLoginRoute } from "@/components/auth/postLoginRoute";

const LOGO = "https://cdn.tranzytours.com/assets/branding/logo.png";

const ADMIN_ROLE_NAMES = new Set([
  "master admin",
  "admin",
  "manager",
  "employee",
]);

/** Present a raw role as a "portal" the user is entering. */
function describePortal(roleName: string): {
  title: string;
  subtitle: string;
  Icon: IconType;
} {
  const norm = roleName.trim().toLowerCase();

  if (norm === "supplier") {
    return {
      title: "Supplier Portal",
      subtitle: "Manage your products, bookings and leads",
      Icon: FiTruck,
    };
  }
  if (norm === "affiliate partner") {
    return {
      title: "Affiliate Portal",
      subtitle: "Track your referrals, bookings and commission",
      Icon: FiUsers,
    };
  }
  if (ADMIN_ROLE_NAMES.has(norm)) {
    return {
      title: `${roleName} — Admin`,
      subtitle: "Internal administration and management",
      Icon: FiShield,
    };
  }
  return {
    title: roleName,
    subtitle: "Continue in this role",
    Icon: FiBriefcase,
  };
}

type SelectPortalState = {
  selectionToken?: string;
  availableRoles?: AvailableRole[];
  redirectTo?: string;
};

export default function SelectPortal() {
  const navigate = useNavigate();
  const location = useLocation();

  const { selectionToken, availableRoles, redirectTo } =
    (location.state as SelectPortalState) || {};

  const [error, setError] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<number | null>(null);

  // Landed here without a valid selection context (refresh / direct hit).
  useEffect(() => {
    if (!selectionToken || !availableRoles || availableRoles.length === 0) {
      navigate("/login", { replace: true });
    }
  }, [selectionToken, availableRoles, navigate]);

  const handlePick = async (role: AvailableRole) => {
    if (!selectionToken || submittingId !== null) return;

    setError(null);
    setSubmittingId(role.id);
    try {
      await selectRole({ selectionToken, roleId: role.id });
      const user = await verifySession().catch(() => null);
      navigate(redirectTo || resolvePostLoginRoute(user), { replace: true });
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Could not continue with the selected portal. Please try again.";
      setError(msg);
      setSubmittingId(null);
    }
  };

  if (!selectionToken || !availableRoles || availableRoles.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0ea5e9]/85 md:bg-[#0ea5e9] relative overflow-hidden">
      <div className="pointer-events-none absolute -left-10 -top-10 h-40 w-40 rounded-full bg-white/30 blur-md" />
      <div className="pointer-events-none absolute -right-12 bottom-8 h-44 w-44 rounded-full bg-white/30 blur-md" />

      <div className="mx-auto max-w-6xl px-4 py-8 md:py-14">
        <div className="relative mx-auto max-w-md rounded-3xl bg-white shadow-[0_25px_80px_rgba(2,6,23,0.25)] overflow-hidden border border-gray-200">
          <div className="px-8 py-10">
            <div className="mb-4 flex items-center justify-center">
              <img src={LOGO} alt="Tranzy Tours" className="h-12" />
            </div>

            <h1 className="text-center text-3xl font-bold text-sky-600">
              Choose a portal
            </h1>
            <p className="mt-1 text-center text-xs text-gray-500">
              Your account has access to more than one portal. Pick where you
              want to go.
            </p>

            {error && (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                <FiAlertCircle className="mt-0.5 shrink-0" />
                <div className="leading-5">{error}</div>
              </div>
            )}

            <div className="mt-6 space-y-3">
              {availableRoles.map((role) => {
                const { title, subtitle, Icon } = describePortal(role.name);
                const busy = submittingId === role.id;
                return (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => handlePick(role)}
                    disabled={submittingId !== null}
                    className="group flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left transition hover:border-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-sky-100 text-sky-600">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-gray-900">
                        {title}
                      </span>
                      <span className="block truncate text-xs text-gray-500">
                        {subtitle}
                      </span>
                    </span>
                    {busy ? (
                      <span className="text-xs font-medium text-sky-600">
                        Entering…
                      </span>
                    ) : (
                      <FiChevronRight className="h-5 w-5 shrink-0 text-gray-400 group-hover:text-sky-500" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 text-center text-xs">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-800"
              >
                <FiArrowLeft /> Back to sign in
              </Link>
            </div>
          </div>

          <div className="h-2 w-full bg-gradient-to-r from-sky-400 via-sky-500 to-sky-600" />
        </div>
      </div>
    </div>
  );
}
